import { FaqQuestion } from '../components/blocks/faqs/types/faqs-types';

/**
 * Generates FAQ JSON-LD schema from FAQ questions
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const generateFaqJsonLd = (faqQuestions: FaqQuestion[]): any => {
    if (!faqQuestions || faqQuestions.length === 0) {
        return null;
    }

    return {
        '@context': 'https://schema.org/',
        '@type': 'FAQPage',
        mainEntity: faqQuestions
            .filter(item => item?.question && item?.answer)
            .map(item => ({
                '@type': 'Question',
                name: item.question,
                acceptedAnswer: {
                    '@type': 'Answer',
                    text: item.answer,
                },
            })),
    };
};

/**
 * Extracts FAQ questions from page data
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
export const extractFaqQuestions = (data: any): FaqQuestion[] => {
    if (!data) return [];

    const allQuestions: FaqQuestion[] = [];
    const seenQuestions = new Set<string>();

    // Helper to add unique questions
    const addUniqueQuestions = (questions: FaqQuestion[]): void => {
        questions.forEach((q) => {
            if (q.question) {
                const normalizedQuestion = q.question.toLowerCase().trim();
                if (!seenQuestions.has(normalizedQuestion)) {
                    seenQuestions.add(normalizedQuestion);
                    allQuestions.push(q);
                }
            }
        });
    };

    // Helper to process question groups
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const processQuestionGroups = (questionGroups: any[]): void => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        questionGroups.forEach((questionGroup: any) => {
            if (questionGroup.questions && Array.isArray(questionGroup.questions)) {
                addUniqueQuestions(questionGroup.questions);
            }
        });
    };

    // Extract from sections (regular pages)
    const sections = data.sections || data.resolvedSections || [];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    sections.forEach((section: any) => {
        // Handle faqs block structure
        if (section.faqs?.questions && Array.isArray(section.faqs.questions)) {
            processQuestionGroups(section.faqs.questions);
        }

        // Handle faqs_grid block structure
        if (section.faqs_grid?.faqs?.questions && Array.isArray(section.faqs_grid.faqs.questions)) {
            processQuestionGroups(section.faqs_grid.faqs.questions);
        }

        // Handle mediacontent_faqs block structure
        if (section.mediacontent_faqs?.faqs?.questions && Array.isArray(section.mediacontent_faqs.faqs.questions)) {
            processQuestionGroups(section.mediacontent_faqs.faqs.questions);
        }
    });

    // Extract from resolvedSections (franchise pages)
    const resolvedSections = data.cmsFranchisePageBuilder?.resolvedSections
    || data.cmsFranchiseServicePages?.resolvedSections
    || [];

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolvedSections.forEach((section: any) => {
        if (section.faqs?.questions?.questions && Array.isArray(section.faqs.questions.questions)) {
            addUniqueQuestions(section.faqs.questions.questions);
        }

        if (section.faqs_grid?.faqs?.questions?.questions && Array.isArray(section.faqs_grid.faqs.questions.questions)) {
            addUniqueQuestions(section.faqs_grid.faqs.questions.questions);
        }

        if (section.mediacontent_faqs?.faqs?.questions?.questions && Array.isArray(section.mediacontent_faqs.faqs.questions.questions)) {
            addUniqueQuestions(section.mediacontent_faqs.faqs.questions.questions);
        }
    });

    // Also check other page structures
    const blocks = data.cmsPage?.pageBuilderBlocks || [];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    blocks.forEach((block: any) => {
        // Handle faqs block structure
        if (block.faqs?.questions && Array.isArray(block.faqs.questions)) {
            processQuestionGroups(block.faqs.questions);
        }

        // Handle faqs_grid block structure
        if (block.faqs_grid?.faqs?.questions && Array.isArray(block.faqs_grid.faqs.questions)) {
            processQuestionGroups(block.faqs_grid.faqs.questions);
        }

        // Handle mediacontent_faqs block structure
        if (block.mediacontent_faqs?.faqs?.questions && Array.isArray(block.mediacontent_faqs.faqs.questions)) {
            processQuestionGroups(block.mediacontent_faqs.faqs.questions);
        }
    });

    return allQuestions;
};

/**
 * Extracts FAQ data and generates JSON-LD schema from page data
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
export const extractFaqData = (data: any): {
    faqQuestions: FaqQuestion[];
    hasFaqQuestions: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    faqJsonLd: any;
} => {
    const faqQuestions = extractFaqQuestions(data);
    const faqJsonLd = generateFaqJsonLd(faqQuestions);

    return {
        faqQuestions,
        hasFaqQuestions: faqQuestions.length > 0,
        faqJsonLd,
    };
};
